import React, { useMemo } from 'react';
import { Card, Typography, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';

import { useGetConstMachineAllocations, useGetVwConstMachines, useGetPersonnel } from '../../../hooks/genericHooks';
import type { ConstMachineAllocation, VwConstMachines, VwPersonnel } from '../../../types';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const MachMachineAllocationsList: React.FC = () => {
  const navigate = useNavigate();

  const { data: allocData, refetch: refetchAllocations } = useGetConstMachineAllocations();
  const { data: machinesData } = useGetVwConstMachines();
  const { data: personnelData } = useGetPersonnel();

  const allocations: ConstMachineAllocation[] = Array.isArray(allocData)
    ? (allocData as any)
    : (allocData?.data || []);
  const machines: VwConstMachines[] = Array.isArray(machinesData)
    ? (machinesData as any)
    : (machinesData?.data || []);
  const persons: VwPersonnel[] = Array.isArray(personnelData)
    ? (personnelData as any)
    : (personnelData?.data || []);

  // Refresh allocations when this list page mounts
  React.useEffect(() => {
    refetchAllocations();
  }, [refetchAllocations]);

  const machineMap = useMemo(() => {
    const m = new Map<number, string>();
    (machines || []).forEach((x) => {
      let specsStr = '';
      if (x.specs && typeof x.specs === 'object') {
        const entries = Object.entries(x.specs).filter(
          ([k, v]) => k && v != null && String(v).trim() !== ''
        );
        if (entries.length) {
          specsStr = entries
            .map(([k, v]) => `${k}: ${String(v)}`)
            .join('; ');
        }
      }
      const baseModel =
        `${x.make || ''} ${x.model || ''}`.trim() || `ID ${x.id}`;
      const label = specsStr ? `${baseModel} – ${specsStr}` : baseModel;
      m.set(x.id, label);
    });
    return m;
  }, [machines]);

  const personMap = useMemo(() => {
    const m = new Map<number, string>();
    (persons || []).forEach((p) => {
      const full = `${p.first_name || ''} ${p.last_name || ''}`.trim();
      m.set(p.id, `${full || 'Unknown'} (${p.pers_id})`);
    });
    return m;
  }, [persons]);

  const columns = [
    {
      title: 'Ref #',
      dataIndex: 'ref_num',
      key: 'ref_num',
      width: 130,
    },
    {
      title: 'Machine',
      dataIndex: 'machine_id',
      key: 'machine_id',
      render: (v: number) => machineMap.get(v) || v,
    },
    {
      title: 'Requester',
      dataIndex: 'requester_personnel_id',
      key: 'requester_name',
      render: (v: number) => {
        const label = personMap.get(v);
        return label || '';
      },
    },
    {
      title: 'Approver',
      dataIndex: 'approver_personnel_id',
      key: 'approver_personnel_id',
      render: (v: number | null | undefined) => {
        if (!v) return '-';
        const label = personMap.get(v);
        return label || '';
      },
    },
    {
      title: 'Start',
      dataIndex: 'start_time',
      key: 'start_time',
    },
    {
      title: 'End',
      dataIndex: 'end_time',
      key: 'end_time',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: ConstMachineAllocation['status']) => {
        let color: string = 'default';
        if (status === 'requested') color = 'blue';
        else if (status === 'approved') color = 'green';
        else if (status === 'deferred') color = 'orange';
        else if (status === 'rejected') color = 'red';
        else if (status === 'cancelled') color = 'volcano';
        else if (status === 'completed') color = 'geekblue';
        return <Tag color={color}>{String(status).toUpperCase()}</Tag>;
      },
    },
    createActionColumn<ConstMachineAllocation>({
      onView: (record) =>
        record.id && navigate(`/machinery/allocations/view/${record.id}`),
      align: 'left',
      showEdit: false,
      showDelete: false,
    }) as any,
  ];

  return (
    <Card
      style={{ margin: 16 }}
      title={
        <Title level={4} style={{ margin: 0 }}>
          Machine Reservations / Allocations
        </Title>
      }
      bordered={false}
      extra={
        <Button action="add" type="primary" onClick={() => navigate('/machinery/allocations/create')}>
          New Reservation
        </Button>
      }
    >
      <StandardTable<ConstMachineAllocation>
        columns={columns as any}
        dataSource={allocations}
        rowKey="id"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
};

export default MachMachineAllocationsList;

